/**
 * 🔒📖 AdminHeroCodex — Owner-only live editor for the Hero Codex.
 *
 * Lets the owner edit per-class meta (tagline, lore, long_desc, block,
 * charisma) and per-skill fields (name, lvl_req, cost, tagline, long_desc)
 * without touching Python source. Overrides save to Mongo and merge into
 * the canon at read time. "Reset to Canon" wipes overrides for a class.
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, RotateCcw, Pencil, Shield, ShieldAlert } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminHeroCodex() {
  const { user, loading: authLoading, isAuthed } = useAuth();
  const [gallery, setGallery] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [classDraft, setClassDraft] = useState({});
  const [skillDrafts, setSkillDrafts] = useState({}); // {skill_id: {...}}
  const [busy, setBusy] = useState(false);

  const isOwner = user && (
    user.role === "owner" ||
    (user.email || "").toLowerCase() === "omnimuscle@yahoo.com"
  );

  useEffect(() => {
    if (!authLoading && isOwner) {
      axios.get(`${API}/hero-codex`).then((r) => setGallery(r.data?.heroes || []));
    }
  }, [authLoading, isOwner]);

  const loadDetail = async (cid) => {
    setActiveId(cid);
    setDetail(null);
    setClassDraft({});
    setSkillDrafts({});
    try {
      const r = await axios.get(`${API}/admin/hero-codex/${cid}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load hero.");
    }
  };

  const allSkills = useMemo(() => {
    if (!detail?.merged?.slots) return [];
    const slots = detail.merged.slots;
    const arr = [];
    if (slots.normal) arr.push({ slot: "Normal", ...slots.normal });
    (slots.secondary || []).forEach((s) => arr.push({ slot: "Secondary", ...s }));
    (slots.passive || []).forEach((s) => arr.push({ slot: "Passive", ...s }));
    (slots.super_passive || []).forEach((s) => arr.push({ slot: "Super Passive", ...s }));
    if (slots.ultimate) arr.push({ slot: "Ultimate", ...slots.ultimate });
    if (slots.super_ultimate) arr.push({ slot: "Super Ultimate", ...slots.super_ultimate });
    return arr;
  }, [detail]);

  const saveClassMeta = async () => {
    if (!Object.keys(classDraft).length) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/hero-codex/${activeId}`, classDraft);
      toast.success("Class meta saved.");
      await loadDetail(activeId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const saveSkill = async (skillId) => {
    const patch = skillDrafts[skillId];
    if (!patch || !Object.keys(patch).length) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/hero-codex/${activeId}/skills/${skillId}`, patch);
      toast.success(`Skill saved: ${skillId}`);
      setSkillDrafts((prev) => { const n = { ...prev }; delete n[skillId]; return n; });
      await loadDetail(activeId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const resetOverrides = async () => {
    if (!window.confirm(`Wipe ALL overrides for ${activeId} and restore canon?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/hero-codex/${activeId}/overrides`);
      toast.success("Restored to canon.");
      await loadDetail(activeId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed.");
    } finally { setBusy(false); }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center text-zinc-500">Loading…</div>
        <Footer />
      </div>
    );
  }
  if (!isAuthed || !isOwner) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <ShieldAlert className="w-12 h-12 mx-auto text-red-400 mb-4" />
          <h1 className="text-2xl font-bold">Owner Only</h1>
          <p className="text-zinc-400 mt-2">This editor is restricted to the owner account.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-7xl" data-testid="admin-hero-codex">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-7 h-7 text-amber-400" />
          <h1 className="text-3xl font-bold">Hero Codex Editor</h1>
          <span className="text-xs text-zinc-500 ml-2">owner-gated · live edit · overrides stored in Mongo</span>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Select value={activeId || ""} onValueChange={loadDetail}>
            <SelectTrigger className="w-72 bg-zinc-900 border-zinc-800" data-testid="admin-hero-picker">
              <SelectValue placeholder="Pick a hero to edit…" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 text-zinc-100">
              {gallery.map((h) => (
                <SelectItem key={h.class_id} value={h.class_id}>
                  {h.icon} {h.name}{h.kit_available ? "" : " (kit pending)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {detail?.overrides && Object.keys(detail.overrides).filter((k) => k !== "class_id" && k !== "updated_at").length > 0 && (
            <>
              <span className="text-xs text-amber-400 px-2 py-1 bg-amber-500/10 border border-amber-500/30 rounded">
                ✎ overrides active
              </span>
              <Button
                variant="ghost" size="sm"
                className="text-red-400 hover:text-red-300"
                onClick={resetOverrides} disabled={busy}
                data-testid="reset-overrides-btn"
              >
                <RotateCcw className="w-4 h-4 mr-1" /> Reset to Canon
              </Button>
            </>
          )}
        </div>

        {!activeId && (
          <div className="text-zinc-500 py-20 text-center">Pick a hero above to start editing.</div>
        )}

        {detail && (
          <div className="space-y-6">
            {/* ───── Class meta editor ───── */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-5 space-y-3">
                <h2 className="text-lg font-bold mb-2 flex items-center gap-2">
                  <Pencil className="w-4 h-4" /> Class Meta — {detail.merged.name}
                </h2>
                <Field
                  label="Tagline"
                  defaultValue={detail.merged.tagline || ""}
                  testid="meta-tagline"
                  onChange={(v) => setClassDraft({ ...classDraft, tagline: v })}
                />
                <Field
                  label="Long description"
                  defaultValue={detail.merged.long_desc || ""}
                  textarea
                  testid="meta-long-desc"
                  onChange={(v) => setClassDraft({ ...classDraft, long_desc: v })}
                />
                <Field
                  label="Lore"
                  defaultValue={detail.merged.lore || ""}
                  textarea
                  testid="meta-lore"
                  onChange={(v) => setClassDraft({ ...classDraft, lore: v })}
                />
                <div className="grid grid-cols-2 gap-3">
                  <NumberField
                    label="Block"
                    defaultValue={detail.merged.stats.block}
                    color="sky"
                    testid="meta-block"
                    onChange={(v) =>
                      setClassDraft({ ...classDraft, base_stats: { ...(classDraft.base_stats || {}), block: v } })
                    }
                  />
                  <NumberField
                    label="Charisma"
                    defaultValue={detail.merged.stats.charisma}
                    color="rose"
                    testid="meta-charisma"
                    onChange={(v) =>
                      setClassDraft({ ...classDraft, base_stats: { ...(classDraft.base_stats || {}), charisma: v } })
                    }
                  />
                </div>
                <div className="pt-2">
                  <Button
                    onClick={saveClassMeta}
                    disabled={busy || !Object.keys(classDraft).length}
                    className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-bold"
                    data-testid="save-class-meta-btn"
                  >
                    <Save className="w-4 h-4 mr-1" /> Save Class Meta
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* ───── Skill editor ───── */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-5">
                <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
                  <Pencil className="w-4 h-4" /> Skills
                </h2>
                {!allSkills.length && (
                  <div className="text-sm text-zinc-500 italic">No skill kit registered for this class yet.</div>
                )}
                <div className="space-y-3">
                  {allSkills.map((sk) => (
                    <SkillEditor
                      key={sk.id}
                      skill={sk}
                      draft={skillDrafts[sk.id] || {}}
                      busy={busy}
                      onChange={(patch) => setSkillDrafts({ ...skillDrafts, [sk.id]: patch })}
                      onSave={() => saveSkill(sk.id)}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

function Field({ label, defaultValue, onChange, textarea, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wide text-zinc-500">{label}</label>
      {textarea ? (
        <Textarea
          defaultValue={defaultValue}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="bg-zinc-950 border-zinc-800 mt-1"
          data-testid={testid}
        />
      ) : (
        <Input
          defaultValue={defaultValue}
          onChange={(e) => onChange(e.target.value)}
          className="bg-zinc-950 border-zinc-800 mt-1"
          data-testid={testid}
        />
      )}
    </div>
  );
}

function NumberField({ label, defaultValue, onChange, color, testid }) {
  const ring = color === "sky" ? "ring-sky-500/30" : color === "rose" ? "ring-rose-500/30" : "ring-zinc-700";
  return (
    <div>
      <label className={`text-xs uppercase tracking-wide ${color === "sky" ? "text-sky-400" : color === "rose" ? "text-rose-400" : "text-zinc-500"}`}>
        {label}
      </label>
      <Input
        type="number" min="0"
        defaultValue={defaultValue}
        onChange={(e) => onChange(Number(e.target.value))}
        className={`bg-zinc-950 border-zinc-800 mt-1 ring-1 ${ring}`}
        data-testid={testid}
      />
    </div>
  );
}

function SkillEditor({ skill, draft, busy, onChange, onSave }) {
  const merged = { ...skill, ...draft };
  const isDirty = Object.keys(draft).length > 0;
  return (
    <div className={`bg-zinc-950 border rounded p-3 ${isDirty ? "border-amber-500/40" : "border-zinc-800"}`}
         data-testid={`skill-editor-${skill.id}`}>
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-xl">{skill.icon}</span>
        <span className="text-[10px] uppercase bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded">{skill.slot}</span>
        <span className="font-mono text-xs text-zinc-500">{skill.id}</span>
        {isDirty && <span className="ml-auto text-[10px] text-amber-400">● unsaved</span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Field
          label="Name"
          defaultValue={merged.name || ""}
          onChange={(v) => onChange({ ...draft, name: v })}
          testid={`skill-${skill.id}-name`}
        />
        <NumberField
          label="Lv Req"
          defaultValue={merged.lvl_req ?? 1}
          onChange={(v) => onChange({ ...draft, lvl_req: v })}
          testid={`skill-${skill.id}-lvl`}
        />
        <NumberField
          label="Cost"
          defaultValue={merged.cost ?? 0}
          onChange={(v) => onChange({ ...draft, cost: v })}
          testid={`skill-${skill.id}-cost`}
        />
      </div>
      <div className="mt-2">
        <Field
          label="Tagline"
          defaultValue={merged.tagline || ""}
          onChange={(v) => onChange({ ...draft, tagline: v })}
          testid={`skill-${skill.id}-tagline`}
        />
      </div>
      <div className="mt-2">
        <Field
          label="Long description"
          defaultValue={merged.long_desc || ""}
          textarea
          onChange={(v) => onChange({ ...draft, long_desc: v })}
          testid={`skill-${skill.id}-long-desc`}
        />
      </div>
      <div className="mt-3">
        <Button
          size="sm"
          onClick={onSave}
          disabled={busy || !isDirty}
          className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-bold"
          data-testid={`save-skill-${skill.id}`}
        >
          <Save className="w-4 h-4 mr-1" /> Save
        </Button>
      </div>
    </div>
  );
}
